import { cn } from '@listening/utils/react-helpers';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type * as React from 'react';

const inputVariants = cva('relative', {
  variants: {
    iconPosition: {
      left: 'absolute top-1/2 left-4 -translate-y-1/2 transform text-muted-foreground',
      right:
        'absolute top-1/2 right-3 left-auto -translate-y-1/2 transform text-muted-foreground',
    },
  },
  defaultVariants: {
    iconPosition: 'left',
  },
});

type InputProps = {
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  wrapperClassName?: string;
} & React.ComponentProps<'input'> &
  VariantProps<typeof inputVariants>;

const Input = ({
  ref,
  className,
  type,
  icon,
  rightIcon,
  iconPosition,
  wrapperClassName,
  ...props
}: InputProps) =>
  icon || rightIcon ? (
    <div
      className={cn(
        'relative inline-flex h-[48px] items-center',
        wrapperClassName,
      )}
    >
      {icon && iconPosition !== 'right' ? (
        <span className={cn(inputVariants({ iconPosition }))}>{icon}</span>
      ) : null}
      <input
        className={cn(
          'flex h-full w-full rounded-md border border-input bg-transparent py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          className,
          icon && iconPosition !== 'right' ? 'pl-11' : 'pl-4',
          rightIcon ? 'pr-10' : 'pr-4',
        )}
        ref={ref}
        type={type}
        {...props}
      />
      {rightIcon ? (
        <span
          className={cn(
            'absolute top-1/2 right-3 -translate-y-1/2 transform text-muted-foreground',
          )}
        >
          {rightIcon}
        </span>
      ) : null}
      {icon && iconPosition === 'right' && !rightIcon ? (
        <span className={cn(inputVariants({ iconPosition }))}>{icon}</span>
      ) : null}
    </div>
  ) : (
    <input
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-transparent px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      ref={ref}
      type={type}
      {...props}
    />
  );
Input.displayName = 'Input';

export { Input };
